import React from 'react'
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
  Collapse,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Paper,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  Home,
  Person,
  Groups,
  Folder,
  Description,
  AttachFile,
  Assessment,
  Logout,
  People,
  ExpandLess,
  ExpandMore,
  Notifications,
  VideoCall,
  Movie,
  BarChart,
  Gavel,
  Shuffle,
  EmojiEvents,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { logout } from '../store/slices/authSlice'
import { hasPermission, getRoleLabel, type Permission } from '../utils/permissions'
import { useGetUnreadCountQuery, useGetNotificationsQuery, useMarkAllAsReadMutation } from '../store/api/notificationsApi'
import Logo from './Logo'
import GlobalDeadlineBanner from './GlobalDeadlineBanner'

const drawerWidth = 240

interface MenuItem {
  text: string
  icon: React.ReactNode
  path?: string
  permission?: Permission
  hideIfHasPermission?: Permission // Ẩn menu nếu user có permission này
  hideIfRole?: string // Ẩn menu nếu user có role này
  allowedRoles?: string[] // Chỉ hiển thị menu nếu user có role trong danh sách này
  children?: MenuItem[]
}

const menuItems: MenuItem[] = [
  { text: 'Trang chủ', icon: <Home />, path: '/dashboard' },
  { text: 'Quản lý người dùng', icon: <People />, path: '/users', permission: 'MANAGE_USERS' },
  { text: 'Đội thi', icon: <Groups />, path: '/teams', hideIfRole: 'judge' },
  { text: 'Cuộc thi', icon: <Folder />, path: '/cases' },
  {
    text: 'Bốc thăm & phân vai',
    icon: <Shuffle />,
    path: '/brackets',
    permission: 'ASSIGN_CASES',
  },
  {
    text: 'Bài nộp',
    icon: <Description />,
    hideIfRole: 'judge',
    children: [
      { text: 'Đơn khởi kiện', icon: <Description />, path: '/memorials' },
      { text: 'Chứng cứ', icon: <AttachFile />, path: '/evidence' },
      { text: 'Video giới thiệu', icon: <Movie />, path: '/intro-video' },
    ],
  },
  { text: 'Chấm điểm', icon: <Assessment />, path: '/scoring', permission: 'SCORE_CASES' },
  {
    text: 'Phân công GK',
    icon: <Gavel />,
    path: '/judge-assignments',
    allowedRoles: ['secretary', 'support', 'judge'],
  },
  { text: 'Virtual Courtroom', icon: <VideoCall />, path: '/courtroom' },
  {
    text: 'Báo cáo và Phân tích',
    icon: <BarChart />,
    path: '/reports',
    allowedRoles: ['secretary', 'support', 'judge'],
  },
  {
    text: 'Bàn Thư ký',
    icon: <Gavel />,
    path: '/secretary-desk',
    allowedRoles: ['secretary'],
  },
  {
    text: 'Phán quyết — Thư ký',
    icon: <Gavel />,
    path: '/verdicts-review',
    allowedRoles: ['secretary', 'support'],
  },
  {
    text: 'Thành tích',
    icon: <EmojiEvents />,
    path: '/achievements',
    allowedRoles: ['student', 'secretary'],
  },
  { text: 'Thông báo', icon: <Notifications />, path: '/notifications' },
  { text: 'Thông tin cá nhân', icon: <Person />, path: '/profile' },
]

export default function Layout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)
  const [openMenus, setOpenMenus] = React.useState<Record<string, boolean>>({})
  const [notificationAnchor, setNotificationAnchor] = React.useState<null | HTMLElement>(null)
  const [locallyCheckedCount, setLocallyCheckedCount] = React.useState<number | null>(null)
  const [markAllAsRead] = useMarkAllAsReadMutation()
  
  // Get unread count and recent notifications with polling
  const { data: unreadCountData } = useGetUnreadCountQuery(undefined, {
    pollingInterval: 20000, // Poll every 20 seconds
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })
  const { data: recentNotifications = [] } = useGetNotificationsQuery(
    { limit: 5 },
    { 
      pollingInterval: 20000, // Poll every 20 seconds
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )
  
  const rawUnreadCount = unreadCountData?.count || 0
  const unreadCount =
    locallyCheckedCount !== null && rawUnreadCount <= locallyCheckedCount ? 0 : rawUnreadCount
  const hasNewNotifications = unreadCount > 0

  React.useEffect(() => {
    if (rawUnreadCount === 0) {
      setLocallyCheckedCount(null)
    } else if (locallyCheckedCount !== null && rawUnreadCount > locallyCheckedCount) {
      setLocallyCheckedCount(null)
    }
  }, [locallyCheckedCount, rawUnreadCount])

  const acknowledgeNotifications = React.useCallback(async () => {
    if (rawUnreadCount <= 0) return
    setLocallyCheckedCount(rawUnreadCount)
    try {
      await markAllAsRead().unwrap()
    } catch (error) {
      setLocallyCheckedCount(null)
      console.error('Mark notifications as read failed:', error)
    }
  }, [markAllAsRead, rawUnreadCount])

  const handleLogout = () => {
    dispatch(logout())
    navigate('/login')
  }

  const handleNotificationClick = async (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget)
    acknowledgeNotifications()
  }

  const handleNotificationClose = () => {
    setNotificationAnchor(null)
  }

  const handleNotificationItemClick = () => {
    handleNotificationClose()
    acknowledgeNotifications()
    navigate('/notifications')
  }

  const handleMenuClick = (item: MenuItem) => {
    if (item.children) {
      setOpenMenus((prev) => ({
        ...prev,
        [item.text]: !prev[item.text],
      }))
    } else if (item.path) {
      if (item.path === '/notifications') {
        acknowledgeNotifications()
      }
      navigate(item.path)
    }
  }

  const isMenuOpen = (text: string) => {
    // Auto-open if any child is active
    const item = menuItems.find((m) => m.text === text)
    if (item?.children) {
      const hasActiveChild = item.children.some(
        (child) => child.path && location.pathname === child.path
      )
      return openMenus[text] || hasActiveChild
    }
    return openMenus[text] || false
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return (
    <Box sx={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          boxShadow: '0 2px 14px rgba(14, 58, 102, 0.38)',
        }}
      >
        <Toolbar sx={{ color: 'inherit' }}>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: 0.3,
            }}
          >
            E-Court Platform
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
              {/* Notification Bell */}
              <IconButton
                onClick={handleNotificationClick}
                sx={{
                  color: 'inherit',
                  '&:hover': {
                    backgroundColor: alpha('#fff', 0.08),
                  },
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  color="secondary"
                  max={99}
                  invisible={!hasNewNotifications}
                >
                  <Notifications />
                </Badge>
              </IconButton>
              
              {/* Notification Menu */}
              <Menu
                anchorEl={notificationAnchor}
                open={Boolean(notificationAnchor)}
                onClose={handleNotificationClose}
                PaperProps={{
                  sx: { width: 360, maxHeight: 400 },
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">Thông báo</Typography>
                  {unreadCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {unreadCount} thông báo chưa đọc
                    </Typography>
                  )}
                </Box>
                {recentNotifications.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      Không có thông báo nào
                    </Typography>
                  </MenuItem>
                ) : (
                  recentNotifications.map((notification) => (
                    <MenuItem
                      key={notification.id}
                      onClick={handleNotificationItemClick}
                      sx={{
                        whiteSpace: 'normal',
                        py: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {notification.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {notification.message.length > 100
                            ? `${notification.message.substring(0, 100)}...`
                            : notification.message}
                        </Typography>
                        {notification.metadata?.meeting_link && (
                          <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                            🔗 Có link phòng trực tuyến
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                )}
                <Divider />
                <MenuItem onClick={handleNotificationItemClick}>
                  <Typography variant="body2" color="primary" sx={{ textAlign: 'center', width: '100%' }}>
                    Xem tất cả thông báo
                  </Typography>
                </MenuItem>
              </Menu>
              
              <Box
                sx={{
                  textAlign: 'right',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: alpha('#fff', 0.12),
                  border: `1px solid ${alpha('#fff', 0.28)}`,
                }}
              >
                <Typography variant="body2" sx={{ color: 'common.white', fontWeight: 600, lineHeight: 1.2 }}>
                  {user.full_name || user.username}
                </Typography>
                <Typography variant="caption" sx={{ color: 'secondary.light', fontSize: '0.7rem', fontWeight: 600 }}>
                  {getRoleLabel(user.role)}
                </Typography>
              </Box>
            </Box>
          )}
          <Button
            onClick={handleLogout}
            startIcon={<Logout />}
            variant="outlined"
            sx={{
              color: 'common.white',
              borderColor: alpha('#fff', 0.55),
              '&:hover': {
                borderColor: 'common.white',
                backgroundColor: alpha('#fff', 0.08),
              },
            }}
          >
            Đăng xuất
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={!isMobile}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            boxShadow: '2px 0 16px rgba(20, 78, 140, 0.08)',
          },
        }}
      >
        <Logo variant="sidebar" />
        <Divider />
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <List sx={{ pt: 1 }}>
            {menuItems.map((item) => {
              // Check allowedRoles if specified
              if (item.allowedRoles && user && !item.allowedRoles.includes(user.role)) {
                return null
              }
              
              // Check permission if required
              if (item.permission && user && !hasPermission(user.role, item.permission)) {
                return null
              }
              
              // Hide if user has specific permission
              if (item.hideIfHasPermission && user && hasPermission(user.role, item.hideIfHasPermission)) {
                return null
              }
              
              // Hide if user has specific role
              if (item.hideIfRole && user && user.role === item.hideIfRole) {
                return null
              }

              // Menu item with children (nested menu)
              if (item.children) {
                const isOpen = isMenuOpen(item.text)
                const hasActiveChild = item.children.some(
                  (child) => child.path && location.pathname === child.path
                )

                return (
                  <React.Fragment key={item.text}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleMenuClick(item)}
                        sx={{
                          mx: 1,
                          mb: 0.5,
                          borderRadius: 2,
                          backgroundColor: hasActiveChild
                            ? 'rgba(20, 78, 140, 0.1)'
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(246, 65, 131, 0.06)',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: hasActiveChild ? 'primary.main' : 'inherit',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontWeight: hasActiveChild ? 600 : 400,
                            color: hasActiveChild ? 'primary.main' : 'inherit',
                          }}
                        />
                        {isOpen ? <ExpandLess /> : <ExpandMore />}
                      </ListItemButton>
                    </ListItem>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((child) => {
                          // Check child permission if required
                          if (
                            child.permission &&
                            user &&
                            !hasPermission(user.role, child.permission)
                          ) {
                            return null
                          }

                          const isActive = !!(child.path && location.pathname === child.path)

                          return (
                            <ListItem key={child.path || child.text} disablePadding>
                              <ListItemButton
                                selected={isActive}
                                onClick={() => child.path && navigate(child.path)}
                                sx={{
                                  mx: 1,
                                  mb: 0.5,
                                  ml: 4,
                                  borderRadius: 2,
                                  '&.Mui-selected': {
                                    background:
                                      'linear-gradient(90deg, rgba(20, 78, 140, 0.12) 0%, rgba(246, 65, 131, 0.1) 100%)',
                                    color: 'primary.main',
                                    borderLeft: '4px solid',
                                    borderColor: 'secondary.main',
                                    '&:hover': {
                                      background:
                                        'linear-gradient(90deg, rgba(20, 78, 140, 0.16) 0%, rgba(246, 65, 131, 0.14) 100%)',
                                    },
                                    '& .MuiListItemIcon-root': {
                                      color: 'primary.main',
                                    },
                                  },
                                  '&:hover': {
                                    backgroundColor: 'rgba(20, 78, 140, 0.06)',
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 40,
                                    color: isActive ? 'primary.main' : 'inherit',
                                  }}
                                >
                                  {child.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.text}
                                  primaryTypographyProps={{
                                    fontWeight: isActive ? 600 : 400,
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          )
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                )
              }

              // Regular menu item
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => item.path && navigate(item.path)}
                    sx={{
                      mx: 1,
                      mb: 0.5,
                      borderRadius: 2,
                      '&.Mui-selected': {
                        background:
                          'linear-gradient(90deg, rgba(20, 78, 140, 0.12) 0%, rgba(246, 65, 131, 0.1) 100%)',
                        color: 'primary.main',
                        borderLeft: '4px solid',
                        borderColor: 'secondary.main',
                        '&:hover': {
                          background:
                            'linear-gradient(90deg, rgba(20, 78, 140, 0.16) 0%, rgba(246, 65, 131, 0.14) 100%)',
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'primary.main',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(246, 65, 131, 0.06)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: location.pathname === item.path ? 'primary.main' : 'inherit',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        item.path === '/notifications' ? (
                          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <Box component="span">{item.text}</Box>
                            {hasNewNotifications && (
                              <Box
                                component="span"
                                aria-label="Có thông báo mới"
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: 'secondary.main',
                                  boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.16)}`,
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          item.text
                        )
                      }
                      primaryTypographyProps={{
                        fontWeight: location.pathname === item.path ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
          </List>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          height: '100dvh',
          bgcolor: 'background.default',
          p: { xs: 2, sm: 3 },
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        <Toolbar />
        <Box sx={{ mx: { xs: -2, sm: -3 }, mt: -3, mb: 3 }}>
          <GlobalDeadlineBanner />
        </Box>
        <Outlet />
      </Box>
    </Box>
  )
}
